import React, { useState, useEffect } from 'react';
import { CheckCircle, RotateCw } from 'lucide-react';

interface RoomSyncIndicatorProps {
  show: boolean;
  roomName: string;
  onClose: () => void;
  isFullscreen?: boolean;
}

const RoomSyncIndicator: React.FC<RoomSyncIndicatorProps> = ({ show, roomName, onClose, isFullscreen = false }) => {
  const [isVisible, setIsVisible] = useState(show);

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      
      // Detect if we're on a mobile device
      const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // Auto-hide faster on mobile devices, especially in fullscreen
      const hideDelay = (isMobile && isFullscreen) ? 1500 : (isMobile ? 2000 : 3000);
      
      const timer = setTimeout(() => {
        setIsVisible(false);
        onClose();
      }, hideDelay);

      return () => clearTimeout(timer);
    }
  }, [show, onClose, isFullscreen]);

  if (!isVisible) return null;

  // Determine positioning based on fullscreen state and screen size
  const getPositionClasses = () => {
    const isMobile = window.innerWidth <= 768;
    
    if (isFullscreen) {
      if (isMobile) {
        // On mobile in fullscreen, position below the exit button to avoid overlap
        return "fixed top-24 right-4 z-40"; // top-24 = 96px, below the exit button at top-12 (48px)
      } else {
        // On desktop in fullscreen, use standard positioning
        return "fixed top-20 right-4 z-50";
      }
    } else {
      // Not in fullscreen, use standard positioning
      return "fixed top-20 right-4 z-50";
    }
  };

  // Adjust width for mobile devices
  const getWidthClasses = () => {
    const isMobile = window.innerWidth <= 768;
    return isMobile ? "min-w-[280px] max-w-[calc(100vw-2rem)]" : "min-w-[300px]";
  };

  return (
    <div className={`${getPositionClasses()} bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg border border-green-600 ${getWidthClasses()}`}>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          <RotateCw size={16} className="animate-spin" />
          <CheckCircle size={16} />
        </div>
        <div className="flex-1">
          <div className="font-medium text-sm">Room Synced!</div>
          <div className="text-xs opacity-90">
            "{roomName}" has been added to surveyor site details
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomSyncIndicator; 